import axios from 'axios'
import Vue from 'vue'
import { isEmpty } from 'lodash-es'
import router from '@/router'
import * as types from '@/store/mutation-types'
import store from '@/store'
import { isTerminalRefreshError, onAuthSessionLogout, onAuthSessionRevoked, refreshAuthToken } from '@/services/auth-session'

onAuthSessionRevoked((message) => {
	if (!Vue.$toast || typeof Vue.$toast.open !== 'function') return
	Vue.$toast.open({
		message,
		type: 'warning',
		position: 'top-right',
		duration: 8000
	})
})

onAuthSessionLogout(() => {
	store.commit(types.LOG_OUT)
	if (store.state.layout !== 'auth') router.push({ name: 'login' })
})

// Global token refresh manager - using window to ensure single instance
if (!window.tokenRefreshManager) {
  window.tokenRefreshManager = {
    isRefreshing: false,
    refreshPromise: null,
    failedQueue: [],
    refreshCount: 0 // Debug counter
  }
}

const processQueue = (error, token = null) => {
  console.log(`[TokenRefresh] Processing queue with ${window.tokenRefreshManager.failedQueue.length} requests`)

  window.tokenRefreshManager.failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })

  window.tokenRefreshManager.failedQueue = []
}

const refreshToken = async (staleToken) => {
  window.tokenRefreshManager.refreshCount++
  console.log(`[TokenRefresh] Starting refresh #${window.tokenRefreshManager.refreshCount}`)

  return refreshAuthToken(instance.defaults.baseURL + '/api/auth/refresh', staleToken)
}

const instance = axios.create()
instance.defaults.baseURL = process.env.API_URL
instance.defaults.headers.common.Accept = '*/*'

// Interceptors
instance.interceptors.request.use(
	(config) => {
		if (localStorage.getItem('token')) {
			config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`
		}
		return config
	}
)
instance.interceptors.response.use(
	response => response,
	async (error) => {
		const status = error.response?.status
		const originalRequest = error.config

		// Only handle 401 errors and avoid infinite loops
		if (status === 401 && originalRequest && !originalRequest.skipAuthRefresh && !originalRequest._retry) {
			originalRequest._retry = true

			console.log(`[TokenRefresh] 401 error detected for ${originalRequest.url}`)

			// Check if user email exists
			if (isEmpty(localStorage.getItem('email'))) {
				console.log('[TokenRefresh] No email found, logging out')
				store.commit(types.LOG_OUT)
				if (store.state.layout !== 'auth') await router.push({ name: 'login' })
				return Promise.reject(error)
			}

			// If already refreshing, add to queue
			if (window.tokenRefreshManager.isRefreshing) {
				console.log('[TokenRefresh] Already refreshing, adding to queue')
				return new Promise((resolve, reject) => {
					window.tokenRefreshManager.failedQueue.push({ resolve, reject })
				}).then(token => {
					originalRequest.headers.Authorization = `Bearer ${token}`
					return instance(originalRequest)
				}).catch(err => {
					return Promise.reject(err)
				})
			}

			// Start refresh process
			console.log('[TokenRefresh] Starting refresh process')
			window.tokenRefreshManager.isRefreshing = true

			if (!window.tokenRefreshManager.refreshPromise) {
				window.tokenRefreshManager.refreshPromise = refreshToken(originalRequest.headers.Authorization?.replace('Bearer ', ''))
			}

			return window.tokenRefreshManager.refreshPromise
				.then(token => {
					console.log('[TokenRefresh] Refresh successful, processing queue')
					processQueue(null, token)
					originalRequest.headers.Authorization = `Bearer ${token}`
					return instance(originalRequest)
				})
				.catch(err => {
					console.log('[TokenRefresh] Refresh failed:', err.message)
					processQueue(err, null)
					if (isTerminalRefreshError(err)) {
						store.commit(types.LOG_OUT)
						if (store.state.layout !== 'auth') router.push({ name: 'login' })
					}
					return Promise.reject(err)
				})
				.finally(() => {
					console.log('[TokenRefresh] Refresh process completed')
					window.tokenRefreshManager.isRefreshing = false
					window.tokenRefreshManager.refreshPromise = null
				})
		}

		return Promise.reject(error)
	}
)
export default instance
